import React from 'react';
import ProjectItem from '../components/ProjectItem';
import { ProjectList } from '../helpers/ProjectList';
import '../styles/Projects.css';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

function ProjectGrid({ projects }) {
  return (
    <motion.div
      className='projectList'
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
    >
      {projects.map((project) => (
        <motion.div key={project.projectId} variants={item}>
          <ProjectItem
            projectId={project.projectId}
            name={project.name}
            image={project.image}
            tagline={project.tagline}
            technologies={project.technologies}
            featured={project.featured}
            status={project.status}
          />
        </motion.div>
      ))}
    </motion.div>
  );
}

// "2015 – 2022", or just "2022" when a group spans a single year
function yearRange(projects) {
  const years = projects.map((p) => p.year);
  const min = Math.min(...years);
  const max = Math.max(...years);
  return min === max ? `${max}` : `${min} – ${max}`;
}

function Projects() {
  const { language } = useLanguage();
  const t = translations[language].projects;

  const caseStudies = ProjectList
    .filter((p) => p.section === 'case-study')
    .sort((a, b) => (a.rank || 99) - (b.rank || 99));
  const recent = ProjectList
    .filter((p) => p.section === 'recent')
    .sort((a, b) => b.year - a.year);
  const archive = ProjectList.filter((p) => !p.section);

  // Group archive projects by year, newest first
  const archiveByYear = {};
  archive.forEach((project) => {
    if (!archiveByYear[project.year]) {
      archiveByYear[project.year] = [];
    }
    archiveByYear[project.year].push(project);
  });

  return (
    <div className='projects'>
      <h1 className='page-title'>{t.title}</h1>

      <div className='projects-section'>
        <h2 className='section-heading'>{t.sectionCaseStudies}</h2>
        <p className='section-subheading'>{yearRange(caseStudies)} · {t.sectionCaseStudiesSub}</p>
        <ProjectGrid projects={caseStudies} />
      </div>

      {recent.length > 0 && (
        <div className='projects-section'>
          <h2 className='section-heading'>{t.sectionRecent}</h2>
          <p className='section-subheading'>{yearRange(recent)} · {t.sectionRecentSub}</p>
          <ProjectGrid projects={recent} />
        </div>
      )}

      <div className='projects-section archive-section'>
        <h2 className='section-heading'>{t.sectionArchive}</h2>
        <p className='section-subheading'>{yearRange(archive)} · {t.sectionArchiveSub}</p>
        {Object.entries(archiveByYear).sort((a, b) => b[0] - a[0]).map(([year, projects]) => (
          <div key={year} className='year-section'>
            <h3 className='year-title'>{year}</h3>
            <ProjectGrid projects={projects} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default Projects;
